import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

/** Calls `onOutside` for pointer presses outside `ref` while `active`. */
export function useClickOutside(ref: RefObject<HTMLElement | null>, onOutside: () => void, active = true): void {
  const handler = useRef(onOutside);
  handler.current = onOutside;

  useEffect(() => {
    if (!active) return undefined;
    const listener = (event: PointerEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) handler.current();
    };
    document.addEventListener('pointerdown', listener);
    return () => document.removeEventListener('pointerdown', listener);
  }, [ref, active]);
}

export function useEscape(onEscape: () => void, active = true): void {
  const handler = useRef(onEscape);
  handler.current = onEscape;

  useEffect(() => {
    if (!active) return undefined;
    const listener = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handler.current();
    };
    document.addEventListener('keydown', listener);
    return () => document.removeEventListener('keydown', listener);
  }, [active]);
}

export function useMediaQuery(query: string): boolean {
  const get = () => typeof window !== 'undefined' && window.matchMedia(query).matches;
  const [matches, setMatches] = useState(get);

  useEffect(() => {
    const list = window.matchMedia(query);
    const update = () => setMatches(list.matches);
    update();
    list.addEventListener('change', update);
    return () => list.removeEventListener('change', update);
  }, [query]);

  return matches;
}

export const MOBILE_QUERY = '(max-width: 760px)';

export function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

const COPIED_MS = 1600;

/** Copies text and reports which key was copied for a short moment. */
export function useCopy(): { copied: string | null; copy: (key: string, text: string) => void } {
  const [copied, setCopied] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = useCallback((key: string, text: string) => {
    void navigator.clipboard?.writeText(text).catch(() => undefined);
    clearTimeout(timer.current);
    setCopied(key);
    timer.current = setTimeout(() => setCopied(null), COPIED_MS);
  }, []);

  return { copied, copy };
}

export function useDocumentTitle(title: string | undefined): void {
  useEffect(() => {
    if (title) document.title = `${title} — zerno`;
  }, [title]);
}

/**
 * Calls `onRestore` when the browser shows this page again from its back/forward
 * cache, e.g. after Back from a payment page, so a button left busy can be used again.
 */
export function useBackForwardRestore(onRestore: () => void): void {
  const handler = useRef(onRestore);
  handler.current = onRestore;

  useEffect(() => {
    const listener = (event: PageTransitionEvent) => {
      if (event.persisted) handler.current();
    };
    window.addEventListener('pageshow', listener);
    return () => window.removeEventListener('pageshow', listener);
  }, []);
}
